import { createContext, useState, useEffect, ReactNode } from 'react';
import { useBusiness } from 'hooks/useBusiness';
import { useLocalization } from 'hooks/useLocalization';
import { Typography } from '@mui/material';
import { ActivityInteractionData, AvailableDatesMonth, ActivityShopUIContextType, PaxQtyAgeGroup } from 'interfaces/ActivityShopUIInterfaces';
import { Activity } from 'interfaces/ActivityInterfaces';
import { ActivitySeason } from "interfaces/ActivityInterfaces";
import GeneralDialog from "components/shared/GeneralDialog";

const ActivityShopUIContext = createContext<ActivityShopUIContextType | undefined>(undefined);

export const ActivityShopUIProvider: React.FC<{children: ReactNode}> = ({ children }) => {
	const [showNotice, setShowNotice] = useState<boolean>(false);
	const [noticeTitle, setNoticeTitle] = useState<string>('');
	const [noticeContent, setNoticeContent] = useState<string | ReactNode>('');
	const [noticeType, setNoticeType] = useState<'error' | 'info' | 'success'>('info');
	const [selectedActivityId, setSelectedActivityId] = useState<number | null>(null);
	const [cartBarWrapperElement, setCartBarWrapperElement] = useState<HTMLDivElement | null>(null);

	const { activities, activityAddons, getBizConfigVar, activityIntervalPriceAdjustments, getActivitySeasons, sendRequest, getExchangeFactor } = useBusiness();
	const { getStr } = useLocalization();

	const [selectedCurrencyCode, setSelectedCurrencyCode] = useState<string>(() => {
		// Get the business configuration variable for currency
		const savedSelectedCurrencyCode = localStorage.getItem('selectedCurrencyCode');
		return savedSelectedCurrencyCode ? savedSelectedCurrencyCode : getBizConfigVar('currency');
	});

	const ageGroupConfig = getBizConfigVar('ageGroups');

	const defaultPaxArr: PaxQtyAgeGroup[] = [
		{ type: 'infant', qty: 0 },
		{ type: 'child', qty: 0 },
		{ type: 'youth', qty: 0 },
		{ type: 'adult', qty: 2 },
		{ type: 'senior', qty: 0 }
	];

	const [paxArr, setPaxArr] = useState<PaxQtyAgeGroup[]>(() => {
		const savedPaxArr = localStorage.getItem('paxArr'); //Try to load the paxArr data from localStorage
		if (savedPaxArr) {
			const parsedPaxArr: PaxQtyAgeGroup[] = JSON.parse(savedPaxArr);

			//Make sure all age groups are still used
			return parsedPaxArr.map((pax) => {
				const ageGroupConfigItem:any = ageGroupConfig.find((ageGroup) => ageGroup.code === pax.type);
				if (ageGroupConfigItem && ageGroupConfigItem.enabled) {
					return pax;
				} else {
					return { type: pax.type, qty: 0 };
				}
			});
		}
		
		return defaultPaxArr;
	});

	const getTotalPaxQty = (paxArrToUse: PaxQtyAgeGroup[]) => {
		let total = 0;
		paxArrToUse.forEach((pax) => {
			total += pax.qty;
		});

		return total;
	};

	const applyDiscount = (price:number, discountPercentage:number) => {
		return price - (price * discountPercentage / 100);
	}

	const applyIntervalPriceAdjustment = (price:number, dateSelected: Date | undefined, agentDiscount: number) => {
		let adjustedPrice = price;
	
		if (dateSelected) {
			activityIntervalPriceAdjustments.forEach((interval) => {
				const startDate = new Date(interval.dates.from);
				const endDate = new Date(interval.dates.to);
		
				if (dateSelected >= startDate && dateSelected <= endDate) {
					adjustedPrice *= 1 + (interval.adjustmentPercentage / 100);
				}
			});
		}
	
		return adjustedPrice - (adjustedPrice * agentDiscount / 100);
	};

	function checkObjectType(obj: any) {
		if (typeof obj === 'string') {
			return 'string';
		} else if (obj instanceof Date) {
			return 'Date';
		} else {
			return 'other';
		}
	}

	const getPriceValues = (itemId: number, itemType: 'tours' | 'addons', dateSelected: Date | undefined, agentSeasons: ActivitySeason[]) => {
		//Check seasons or seasons for price values (base and pp for private/group)
		let groupPP = 0;
		let privateBase = 0;
		let privatePP = 0;

		if (dateSelected && checkObjectType(dateSelected) === 'string') {
			console.error('Date object is stored as string in localStorage, convert it to Date object before using it in getPriceValues()');
		}

		if (dateSelected && checkObjectType(dateSelected) === 'Date') {
		//	throw new Error('Now it workss');
		}

		const dateToCheck = dateSelected ? dateSelected : new Date();
		const activitySeasons = agentSeasons.length ? agentSeasons : getActivitySeasons();

		if (activitySeasons.length > 0) {
			//Find the season in which dateToCheck is between activitySeason.dates.from and activitySeason.dates.to
			
			const activitySeason = activitySeasons.find((activitySeason) => dateToCheck >= activitySeason.dates.from && dateToCheck <= activitySeason.dates.to);

			if (activitySeason && activitySeason.prices?.[itemType]?.[itemId]) {
				const itemPriceVars = activitySeason.prices?.[itemType]?.[itemId];

				if (itemPriceVars) {
					groupPP = itemPriceVars.group?.pp || 0;
					privateBase = itemPriceVars.private?.base || 0;
					privatePP = itemPriceVars.private?.pp || 0;
				}
			}
		} else { //No seasons found, use default prices
			switch (itemType) {
				case 'tours':
					const activity = activities.find((activity) => activity.id === itemId);

					if (activity) {
						groupPP = activity.prices.group?.pp || 0;
						privateBase = activity.prices.private?.base || 0;
						privatePP = activity.prices.private?.pp || 0;
					}

					break;

				case 'addons':
					const addon = activityAddons.find((addon) => addon.id === itemId);

					if (addon) {
						groupPP = addon.prices.group?.pp || 0;
						privateBase = addon.prices.private?.base || 0;
						privatePP = addon.prices.private?.pp || 0;
					}

					break;
			}
		}

		return { groupPP, privateBase, privatePP };
	};

	//Add insufficient pax qty cost to private tours
	const addMinimumPrivateTourCost = (activityType: 'private' | 'group', privatePP: number, paxArrToUse: PaxQtyAgeGroup[]) => {
		if (activityType === 'private') {
			const privateTourMinPax = getBizConfigVar('privateTourMinPax');
			const totalPaxQty = getTotalPaxQty(paxArrToUse);
	
			if (totalPaxQty < privateTourMinPax) {
				const missingAdults = privateTourMinPax - totalPaxQty;
				return missingAdults * privatePP;
			}
		}

		return 0;
	};

	const getActivityPrice = (activityId: number, activityType: 'private' | 'group', paxArrToUse: PaxQtyAgeGroup[], currencyCode: string, dateSelected: Date | undefined, agentDiscount: number = 0, agentSeasons: ActivitySeason[] = []) => {
		const { groupPP, privateBase, privatePP } = getPriceValues(activityId, 'tours', dateSelected, agentSeasons);

		let cartItemPrice = 0;
		const activity = activities.find((activity) => activity.id === activityId);
		const exchangeFactor = agentSeasons.length ? 1 : getExchangeFactor(currencyCode); //If agent seasons are used, prices will already be in agent currency

		if (activity) {
			if (activityType === 'private' && activity.prices.private) {
				cartItemPrice += privateBase;
			}
	
			paxArrToUse.forEach((ageGroupInCart) => {
				const ageGroupConfig = getBizConfigVar('ageGroups').find((ageGroup) => ageGroup.code === ageGroupInCart.type);
	
				if (ageGroupConfig) {
					if (ageGroupConfig.enabled && ageGroupInCart.qty > 0) {
						const discountPercentage = ageGroupConfig.discountPercentage;
		
						if (activityType === 'group') {
							cartItemPrice += ageGroupInCart.qty * applyDiscount(groupPP, discountPercentage);
						} else if (activity.prices.private) {
							cartItemPrice += ageGroupInCart.qty * applyDiscount(privatePP, discountPercentage);
						}
					}
				} else {
					throw new Error('Age group not found in business config');
				}
			});

			//Add the minimum pax qty cost to private tours
			cartItemPrice += addMinimumPrivateTourCost(activityType, privatePP, paxArrToUse);
		}

		return applyIntervalPriceAdjustment((cartItemPrice / exchangeFactor), dateSelected, agentDiscount);
	};

	const getActivityAddonPrice = (addonId: number, activityType: 'private' | 'group', paxArrToUse: PaxQtyAgeGroup[], currencyCode: string, dateSelected: Date | undefined, agentDiscount: number = 0, agentSeasons: ActivitySeason[] = []) => {
		const { groupPP, privateBase, privatePP } = getPriceValues(addonId, 'addons', dateSelected, agentSeasons);

		let addonPrice = 0;
		const addon = activityAddons.find((addon) => addon.id === addonId);
		const exchangeFactor = agentSeasons.length ? 1 : getExchangeFactor(currencyCode); //If agent seasons are used, prices will already be in agent currency

		if (addon) {
			if (activityType === 'private') {
				addonPrice += privateBase;
			}

			paxArrToUse.forEach((ageGroupInCart) => {
				const ageGroupConfig = getBizConfigVar('ageGroups').find((ageGroup) => ageGroup.code === ageGroupInCart.type);

				if (ageGroupConfig) {
					if (ageGroupConfig.enabled && ageGroupInCart.qty > 0) {
						const discountPercentage = addon.isAffectedByAgeGroup ? ageGroupConfig.discountPercentage : 0;
						
						if (activityType === 'group') {
							addonPrice += ageGroupInCart.qty * applyDiscount(groupPP, discountPercentage);
						} else {
							addonPrice += ageGroupInCart.qty * applyDiscount(privatePP, discountPercentage);
						}
					}
				} else {
					throw new Error('Age group not found in business config');
				}
			});
			
			//Add the minimum pax qty cost to private tour add-ons
			addonPrice += addMinimumPrivateTourCost(activityType, privatePP, paxArrToUse);
		}

		return applyIntervalPriceAdjustment(addonPrice / exchangeFactor, dateSelected, agentDiscount);
	}

	const generateNewAddonInteractionData = (addonId: number, activityId: number, activityType: 'private' | 'group') => {
		return {
			addonId: addonId, 
			totalPrice: getActivityAddonPrice(addonId, 'private', paxArr, selectedCurrencyCode, undefined), 
			isAdded: false
		};
	};

	const generateNewActivitiesInteractionData = (activity: Activity) => {
		const newActivitiesInteractionData: ActivityInteractionData = {
			activityId: activity.id,
			isAdded: false,
			addedAs: null,
			totalPrice: {
				private: getActivityPrice(activity.id, 'private', paxArr, selectedCurrencyCode, undefined, 0),
				group: getActivityPrice(activity.id, 'group', paxArr, selectedCurrencyCode, undefined, 0),
			},
			addons: {
				//Add addons that are available for the corresponding activity type by checking addon.availableAsTourType.private and addon.availableAsTourType.group
				private: activity.addonIds.flatMap(addonId => 
					activityAddons
						.filter(addon => addon.id === addonId && addon.availableAsTourType.private)
						.map(addon => (generateNewAddonInteractionData(addon.id, activity.id, 'private')))
				),
				group: activity.addonIds.flatMap(addonId => 
					activityAddons
						.filter(addon => addon.id === addonId && addon.availableAsTourType.group)
						.map(addon => (generateNewAddonInteractionData(addon.id, activity.id, 'group')))
				),
			},
			dateSelected: {
				private: undefined,
				group: undefined,
			},
			timeSelected: {
				private: null,
				group: null,
			},
			departureIdSelected: null,
			availableDates: {
				group: [],
			}
		};

		return newActivitiesInteractionData;
	};

	const [activitiesInteractionData, setActivitiesInteractionData] = useState<ActivityInteractionData[]>(() => {
		const unparsedSavedActivitiesInteractionData = localStorage.getItem('activitiesInteractionData');
		const currentDate = new Date();
		let removedActivityIds: number[] = [];

		if (unparsedSavedActivitiesInteractionData) {
			const parsedActivitiesInteractionData: ActivityInteractionData[] = JSON.parse(unparsedSavedActivitiesInteractionData);

			// Convert date strings back to Date objects
			const fixedDatesSavedActivitiesInteractionData = parsedActivitiesInteractionData.map(activityData => {
				return {
					...activityData,
					dateSelected: {
						private: (activityData.dateSelected.private && activityData.isAdded && activityData.addedAs === 'private') ? new Date(activityData.dateSelected.private) : undefined,
						group: (activityData.dateSelected.group && activityData.isAdded && activityData.addedAs === 'group') ? new Date(activityData.dateSelected.group) : undefined,
					}
				};
			});

			//Now with dates made into Date objects, update the prices
			const fixedPricesSavedActivitiesInteractionData = fixedDatesSavedActivitiesInteractionData.map(activityData => {
				const updatedTotalPrice = {
					private: getActivityPrice(activityData.activityId, 'private', paxArr, selectedCurrencyCode, activityData.dateSelected.private),
					group: getActivityPrice(activityData.activityId, 'group', paxArr, selectedCurrencyCode, activityData.dateSelected.group),
				};
	
				const updatedAddons = {
					private: activityData.addons.private.map(addon => ({
						...addon,
						totalPrice: getActivityAddonPrice(addon.addonId, 'private', paxArr, selectedCurrencyCode, undefined),
					})),
					group: activityData.addons.group.map(addon => ({
						...addon,
						totalPrice: getActivityAddonPrice(addon.addonId, 'group', paxArr, selectedCurrencyCode, undefined),
					})),
				};

				// Remove past dates and update cart status
				let isAdded = activityData.isAdded;
				let addedAs = activityData.addedAs;
				let dateSelected = activityData.dateSelected;
	
				if (dateSelected.private && dateSelected.private < currentDate) {
					isAdded = false;
					addedAs = null;
					dateSelected.private = undefined;
					removedActivityIds.push(activityData.activityId);
				}
				if (dateSelected.group && dateSelected.group < currentDate) {
					isAdded = false;
					addedAs = null;
					dateSelected.group = undefined;
					removedActivityIds.push(activityData.activityId);
				}
	
				return {
					...activityData,
					isAdded,
					addedAs,
					totalPrice: updatedTotalPrice,
					addons: updatedAddons,
					dateSelected
				};
			});

			const newActivitiesInteractionData = activities.map(activity => {
				const savedActivityInteractionData = fixedPricesSavedActivitiesInteractionData.find(activityData => activityData.activityId === activity.id);
				if (savedActivityInteractionData) {
					// Check for new add-ons and add them if not present in the saved data
					const updatedAddonsPrivate = activity.addonIds
						.filter(addonId => activityAddons.find(addon => addon.id === addonId && addon.availableAsTourType.private) && 
							!savedActivityInteractionData.addons.private.some(addon => addon.addonId === addonId))
						.map(addonId => generateNewAddonInteractionData(addonId, activity.id, 'private'));
	
					const updatedAddonsGroup = activity.addonIds
						.filter(addonId => activityAddons.find(addon => addon.id === addonId && addon.availableAsTourType.group) && 
							!savedActivityInteractionData.addons.group.some(addon => addon.addonId === addonId))
						.map(addonId => generateNewAddonInteractionData(addonId, activity.id, 'group'));
	
					return {
						...savedActivityInteractionData,
						addons: {
							private: [...savedActivityInteractionData.addons.private, ...updatedAddonsPrivate],
							group: [...savedActivityInteractionData.addons.group, ...updatedAddonsGroup]
						}
					};
				}
	
				return generateNewActivitiesInteractionData(activity);
			});

			return newActivitiesInteractionData;
		} else {
			// Initialize with default values if not found in localStorage
			return activities.map(activity => (generateNewActivitiesInteractionData(activity)));
		}
	});

	const handleCurrencyChange = (newCurrencyCode: string) => {
		setSelectedCurrencyCode(newCurrencyCode);
		updateActivitiesPrices(activitiesInteractionData, setActivitiesInteractionData, paxArr, newCurrencyCode);
	};

	const getItemsInCartQty = () => {
		return activitiesInteractionData.reduce((acc, activityInteractionData) => {
			const { isAdded, addedAs } = activityInteractionData;
			if (isAdded && addedAs) {
				acc += 1;
			}
			return acc;
		}, 0);
	}
	
	const applyAgentDiscount = (
		agentDiscount: number,
		currencyCodeToUse: string,
		activitiesInteractionDataToUse: ActivityInteractionData[],
		setActivitiesInteractionDataToUse: React.Dispatch<React.SetStateAction<ActivityInteractionData[]>>
	) => {
		updateActivitiesPrices(activitiesInteractionDataToUse, setActivitiesInteractionDataToUse, paxArr, currencyCodeToUse, agentDiscount);
	};

	const applyAgentSeasons = (
		agentSeasons: ActivitySeason[],
		currencyCodeToUse: string,
		activitiesInteractionDataToUse: ActivityInteractionData[],
		setActivitiesInteractionDataToUse: React.Dispatch<React.SetStateAction<ActivityInteractionData[]>>
	) => {
		updateActivitiesPrices(activitiesInteractionDataToUse, setActivitiesInteractionDataToUse, paxArr, currencyCodeToUse, 0, agentSeasons);
	};

	const updateActivitiesPrices = (
		activitiesInteractionDataToUse: ActivityInteractionData[],
		setActivitiesInteractionDataToUse: React.Dispatch<React.SetStateAction<ActivityInteractionData[]>>,
		paxArrToUse: PaxQtyAgeGroup[],
		currencyCode: string,
		agentDiscount: number = 0,
		agentSeasons: ActivitySeason[] = []
	) => {
		const newActivitiesInteractionData = activitiesInteractionDataToUse.map(activityData => {
			return {
				...activityData,
				totalPrice: {
					private: getActivityPrice(activityData.activityId, 'private', paxArrToUse, currencyCode, activityData.dateSelected.private, agentDiscount, agentSeasons),
					group: getActivityPrice(activityData.activityId, 'group', paxArrToUse, currencyCode, activityData.dateSelected.group, agentDiscount, agentSeasons),
				},
				addons: {
					private: activityData.addons.private.map(addon => ({ ...addon, totalPrice: getActivityAddonPrice(addon.addonId, 'private', paxArrToUse, currencyCode, activityData.dateSelected.private, agentDiscount, agentSeasons) })),
					group: activityData.addons.group.map(addon => ({ ...addon, totalPrice: getActivityAddonPrice(addon.addonId, 'group', paxArrToUse, currencyCode, activityData.dateSelected.group, agentDiscount, agentSeasons) })),
				},
			};
		});

		setActivitiesInteractionDataToUse(newActivitiesInteractionData);
	};

	useEffect(() => {
		// Update localStorage with the current state of activitiesInteractionData
		localStorage.setItem('activitiesInteractionData', JSON.stringify(activitiesInteractionData));
	}, [activitiesInteractionData]);

	const fetchSelectedDeparture = (activityData: ActivityInteractionData) => {
		if (!activityData.dateSelected.group) return;

		//Get year, month and day numbers of selected date
		const yearSelected = activityData.dateSelected.group.getFullYear();
		const monthSelected = activityData.dateSelected.group.getMonth() + 1; // Months are 0-indexed
		const daySelected = activityData.dateSelected.group.getDate();

		//Check if new pax qty is over availability
		//activityData.departureIdSelected

		const yearItem = activityData.availableDates.group.find((yearItem) => yearItem.year === yearSelected);

		if (yearItem) {
			const monthItem = yearItem.months.find((monthItem) => monthItem.month === monthSelected);

			if (monthItem) {
				const dayItem = monthItem.days.find((dayItem) => dayItem.dayNum === daySelected);

				if (dayItem && dayItem.departures) {
					return dayItem.departures.find((departure) => departure.time === activityData.timeSelected.group);
				}
			}
		}
	};

	const modifyPax = (type:string, isIncrement:boolean) => {
		//Check if pax qty is within limits
		if (isIncrement && getPaxQtyOfType(type) === 100) return;
		if (!isIncrement && ((type === 'adult' && getPaxQtyOfType(type) === 1) || (type !== 'adult' && getPaxQtyOfType(type) === 0))) return;

		let updatedActivitiesInteractionData;

		if (isIncrement) { //Check if total pax qty is ok with all selected dates for group tours. If group tour in cart is over limit, remove from cart and give message
			const newPaxQty = getTotalPaxQty(paxArr) + 1;

			const groupActivityIdsInsufficientAvailabiliyInCart: number[] = [];
			const groupActivityIdsToRemoveDates: number[] = [];

			activitiesInteractionData.forEach((activityData) => {
				if (activityData.dateSelected.group && activityData.departureIdSelected) { //This activity has group tour selected
					const departure = fetchSelectedDeparture(activityData);

					if (departure) {
						//Get total availability for this departure by doing (group.capacity - group.occupancy) for all departure.groups
						const totalDepartureAvailability = departure.groups.reduce((acc, group) => acc + (group.capacity - group.occupancy), 0);

						if (newPaxQty > totalDepartureAvailability) {
							groupActivityIdsToRemoveDates.push(activityData.activityId); //Remove date selection of these activities
							
							if (activityData.addedAs === 'group') {
								groupActivityIdsInsufficientAvailabiliyInCart.push(activityData.activityId);
							}
						}
					}
				}
			});

			if (groupActivityIdsInsufficientAvailabiliyInCart.length > 0) { //Has insufficient availability for some cart items - remove from cart
				setShowNotice(true);
				setNoticeTitle(getStr('Activities_removed_from_cart'));
				setNoticeType('error');
				setNoticeContent(
					<>
						<Typography>
							{getStr('ACTIVITIES_REMOVED_FROM_CART_INSUF_AVAIL_INFO')}
						</Typography>
						<ul style={{ marginTop: '20px' }}>
							{
								groupActivityIdsInsufficientAvailabiliyInCart.map((activityId) => {
									const activity = activities.find((activity) => activity.id === activityId);

									if (!activity) return null;

									return (
										<li key={activityId}>{activity.title}</li>
									)
								})
							}
						</ul>
					</>
				);
				
				//Remove items from cart
				updatedActivitiesInteractionData = activitiesInteractionData.map((activityData) => {
					if (groupActivityIdsInsufficientAvailabiliyInCart.includes(activityData.activityId)) {
						return {
							...activityData,
							isAdded: false,
							addedAs: null
						};
					}

					return activityData;
				});
			}

			if (groupActivityIdsToRemoveDates.length > 0) { //Some activities have selected dates that are unavailable for selected departure. Silently remove selected dates from these activities
				updatedActivitiesInteractionData = (updatedActivitiesInteractionData ? updatedActivitiesInteractionData : activitiesInteractionData).map((activityData) => {
					if (groupActivityIdsToRemoveDates.includes(activityData.activityId)) {
						return {
							...activityData,
							dateSelected: {
								...activityData.dateSelected,
								group: undefined,
							},
						};
					}

					return activityData;
				});
			}
		}
		
		const newPaxArr = paxArr.map((pax) => {
			if (pax.type === type) {
				return { type: type, qty: isIncrement ? pax.qty + 1 : pax.qty - 1 };
			}

			return pax;
		});

		setPaxArr(newPaxArr);
		updateActivitiesPrices(
			updatedActivitiesInteractionData ? updatedActivitiesInteractionData : activitiesInteractionData,
			setActivitiesInteractionData,
			newPaxArr,
			selectedCurrencyCode
		);
	}

	const addToCart = (activityId: number, activityType: 'private' | 'group') => {
		const newActivitiesInteractionData = activitiesInteractionData.map((activityData) => {
			if (activityData.activityId === activityId) {
				return {
					...activityData,
					isAdded: true,
					addedAs: activityType,
				};
			}

			return activityData;
		});

		setActivitiesInteractionData(newActivitiesInteractionData);
	};

	const removeFromCart = (activityId:number) => {
		const newActivitiesInteractionData = activitiesInteractionData.map((activityData) => {
			if (activityData.activityId === activityId) {
				return {
					...activityData,
					isAdded: false,
					addedAs: null
				};
			}

			return activityData;
		});

		setActivitiesInteractionData(newActivitiesInteractionData);
	};

	const getTotalPrice = (activitiesInteractionDataToUse: ActivityInteractionData[]) => {
		let total = 0;
		
		activitiesInteractionDataToUse.forEach((activityData) => {
			if (activityData.isAdded) {
				total += activityData.totalPrice[activityData.addedAs as 'private' | 'group'];
				total += activityData.addons[activityData.addedAs as 'private' | 'group'].reduce((acc, addon) => acc + (addon.isAdded ? addon.totalPrice : 0), 0);
			}
		});

		return total;
	}

	useEffect(() => {
		// Persist paxArr changes to localStorage
		localStorage.setItem('paxArr', JSON.stringify(paxArr));
	}, [paxArr]);

	useEffect(() => {
		// Persist paxArr changes to localStorage
		localStorage.setItem('selectedCurrencyCode', selectedCurrencyCode);
	}, [selectedCurrencyCode]);

	const getPaxQtyOfType = (type:string) => {
		const pax = paxArr.find((pax) => pax.type === type);
		return pax ? pax.qty : 0;
	}

	const updateActivityDate = (activityId: number, type: 'private' | 'group', newDate: Date | undefined) => {
		//Generate new prices, in case the date affects the price
		setActivitiesInteractionData(currentActivitiesInteractionData =>
			currentActivitiesInteractionData.map(activityData => {
				if (activityData.activityId === activityId) {
					const dateSelectedPrivate = (type === 'private') ? newDate : activityData.dateSelected.private;
					const dateSelectedGroup = (type === 'group') ? newDate : activityData.dateSelected.group;

					let departureIdSelected = null;
					let timeSelected = null;

					if (type === 'group' && newDate) {
						const selectedYear = newDate.getFullYear();
						const selectedMonth = newDate.getMonth() + 1; // Months are 0-indexed
						const selectedDay = newDate.getDate();
	
						// Attempt to find the first departure for the new date
						const availableDepartures = activityData.availableDates.group.find(year => year.year === selectedYear)?.months.find(month => month.month === selectedMonth)?.days.find(day => day.dayNum === selectedDay)?.departures;
						if (availableDepartures && availableDepartures.length > 0) {
							departureIdSelected = availableDepartures[0].id; // Select the first departure ID
							timeSelected = availableDepartures[0].time; // Select the first time
						}
					}

					return {
						...activityData,
						departureIdSelected: departureIdSelected,
						dateSelected: {
							...activityData.dateSelected,
							[type]: newDate,
						},
						timeSelected: {
							...activityData.timeSelected,
							[type]: timeSelected,
						},
						totalPrice: {
							private: getActivityPrice(activityId, 'private', paxArr, selectedCurrencyCode, dateSelectedPrivate, 0),
							group: getActivityPrice(activityId, 'group', paxArr, selectedCurrencyCode, dateSelectedGroup, 0),
						},
						addons: {
							private: activityData.addons.private.map(addon => {
								const addonTotalPrice = getActivityAddonPrice(addon.addonId, 'private', paxArr, selectedCurrencyCode, dateSelectedPrivate);

								return {
									...addon,
									isAdded: (addonTotalPrice > 0 ? addon.isAdded : false),
									totalPrice: addonTotalPrice
								}}),
							group: activityData.addons.group.map(addon => {
								const addonTotalPrice = getActivityAddonPrice(addon.addonId, 'group', paxArr, selectedCurrencyCode, dateSelectedGroup);

								return {
									...addon,
									isAdded: (addonTotalPrice > 0 ? addon.isAdded : false),
									totalPrice: addonTotalPrice
								}
							}),
						},
					};
				}

				return activityData;
			})
		);
	};

	const updateActivityDeparture = (activityId: number, newDepartureId: number | null, newDepartureTime: string | null) => {
		const newActivitiesInteractionData = activitiesInteractionData.map(activityData => {
			if (activityData.activityId === activityId) {
				return {
					...activityData,
					departureIdSelected: newDepartureId,
					timeSelected: {
						...activityData.timeSelected,
						group: newDepartureTime
					}
				};
			}

			return activityData;
		});

		setActivitiesInteractionData(newActivitiesInteractionData);
	}

	const toggleAddonSelection = (activityId: number, type: 'private' | 'group', addonId: number, isAdded: boolean) => {
		const newActivitiesInteractionData = activitiesInteractionData.map(activityData => {
			if (activityData.activityId === activityId) {
				return {
					...activityData,
					addons: {
						...activityData.addons,
						[type]: activityData.addons[type].map(addon => {
							if (addon.addonId === addonId) {
								return {
									...addon,
									isAdded: isAdded,
								};
							}

							return addon;
						}),
					},
				};
			}

			return activityData;
		});

		setActivitiesInteractionData(newActivitiesInteractionData);
	}

	const getActivityInteractionData = (activityId: number) => {
		const activityInteractionData = activitiesInteractionData.find(activityData => activityData.activityId === activityId);
		if (activityInteractionData) {
			return activityInteractionData;
		} else { //Activity is new - create new activityInteractionData
			const newActivity = activities.find(activity => activity.id === activityId);
			
			if (!newActivity) {
				throw new Error('Activity not found');
			}

			return generateNewActivitiesInteractionData(newActivity);
		}
	}

	const getActivityAvailableMonthDates = (
		activityId: number,
		activityType: 'private' | 'group',
		monthToFetch: number,
		yearToFetch: number
	) => {
		let hasUpdated = false;
		
		const activityInteractionData = getActivityInteractionData(activityId);
		
		// Check if the year already exists
		const availableYearIndex = activityInteractionData.availableDates.group.findIndex(y => y.year === yearToFetch);

		if (availableYearIndex !== -1) {	// Year found, now check for the month
			const monthExists = activityInteractionData.availableDates.group[availableYearIndex].months.some(m => m.month === monthToFetch);

			if (!monthExists) {
				activityInteractionData.availableDates.group[availableYearIndex].months.push({
					month: monthToFetch,
					isLoading: true,
					fetchError: null,
					isSoldOut: false,
					days: []
				});

				hasUpdated = true;
			}
		} else { // Year doesn't exist, add both year and month
			activityInteractionData.availableDates.group.push({
				year: yearToFetch,
				months: [{
					month: monthToFetch,
					isLoading: true,
					fetchError: null,
					isSoldOut: false,
					days: []
				}]
			});

			hasUpdated = true;
		}

		let matchFound = false;

		const updatedActivitiesInteractionData = activitiesInteractionData.map(activityData => {
			if (activityData.activityId === activityId) {
				matchFound = true;
				return { ...activityInteractionData }; // Return updated activity data
			}
			
			return activityData; // Return unmodified activity data for other activities
		});
		
		if (!matchFound) {
			updatedActivitiesInteractionData.push({ ...activityInteractionData });
		}
	
		//There's a new year and/or month, update the state
		if (hasUpdated) {
			setActivitiesInteractionData(updatedActivitiesInteractionData);
			fetchNewMonth(activityId, monthToFetch, yearToFetch);
		}

		// Now, find and return the updated monthDates for the specific activity, year, and month
		const updatedActivityInteractionData = updatedActivitiesInteractionData.find(activityData => activityData.activityId === activityId);
		const availableYear = updatedActivityInteractionData?.availableDates.group.find(year => year.year === yearToFetch);
		const monthDates:AvailableDatesMonth | undefined = availableYear?.months.find(m => m.month === monthToFetch);
	
		// If the monthDates is undefined, throw an error
		if (!monthDates) {
			throw new Error('Month "' + monthToFetch + '" not found in year "' + yearToFetch + '"');
		}

		return monthDates;
	}

	const removeAllGroupMonthData = (activityId: number) => {
		const updatedActivitiesInteractionData = activitiesInteractionData.map(activityData => {
			if (activityData.activityId === activityId) {
				return {
					...activityData,
					availableDates: {
						group: []
					}
				};
			}

			return activityData;
		});

		setActivitiesInteractionData(updatedActivitiesInteractionData);
	}

	const setMonthError = (activityId: number, month: number, year: number, error: string) => {
		const updatedActivitiesInteractionData = activitiesInteractionData.map(activityData => {
			if (activityData.activityId === activityId) {
				const availableYearIndex = activityData.availableDates.group.findIndex(y => y.year === year);
				if (availableYearIndex !== -1) {
					// Clone the months array to avoid direct state mutation
					const updatedMonths = [...activityData.availableDates.group[availableYearIndex].months];
					const monthIndex = updatedMonths.findIndex(m => m.month === month);
					if (monthIndex !== -1) {
						// Set loading to false for the found month
						updatedMonths[monthIndex] = { ...updatedMonths[monthIndex], isLoading: false, fetchError: error };
						// Update the year with the new months array
						activityData.availableDates.group[availableYearIndex] = {
							...activityData.availableDates.group[availableYearIndex],
							months: updatedMonths
						};
					}
				}
				return { ...activityData };
			}
			return activityData;
		});

		// Update the activitiesInteractionData state with the new data
		setActivitiesInteractionData(updatedActivitiesInteractionData);
	}

	const fetchNewMonth = (activityId: number, month: number, year: number) => {
		sendRequest({
			type: 'groupMonthAvailability',
			params: {
				activityId: activityId,
				year: year,
				month: month
			},
			onSuccess: (data) => {
				const updatedActivitiesInteractionData = activitiesInteractionData.map(activityData => {
					if (activityData.activityId === activityId) {
						const availableYearIndex = activityData.availableDates.group.findIndex(y => y.year === year);
						if (availableYearIndex !== -1) {
							// Clone the months array to avoid direct state mutation
							const updatedMonths = [...activityData.availableDates.group[availableYearIndex].months];
							const monthIndex = updatedMonths.findIndex(m => m.month === month);

							if (monthIndex !== -1) {
								// Set loading to false for the found month
								updatedMonths[monthIndex] = { ...updatedMonths[monthIndex], isLoading: false, days: data.groupMonthAvailability.days };

								// Check if the selected date is still available
								const selectedDate = activityData.dateSelected.group;

								if (activityData.isAdded && activityData.addedAs === 'group' && selectedDate) {
									const selectedDay = updatedMonths[monthIndex].days.find(day => day.dayNum === selectedDate.getDate());

									// Ensure selectedDay is found
									if (selectedDay) {
										// Find the specific departure with the selected departure ID
										const selectedDeparture = selectedDay.departures.find(departure => departure.id === activityData.departureIdSelected);

										if (selectedDeparture) {
											const totalPaxQty = getTotalPaxQty(paxArr);
											const departureAvailability = selectedDeparture.groups.reduce((sum, group) => sum + (group.capacity - group.occupancy), 0);

											const hasSufficientAvailability = departureAvailability >= totalPaxQty;

											// Set isAdded to false if no availability
											if (!hasSufficientAvailability) {
												activityData.isAdded = false;
												activityData.dateSelected.group = undefined;

												setNoticeTitle(getStr('No_availability'));
												setNoticeContent(getStr('NO_AVAILABILITY_ANYMORE_INFO'));
												setNoticeType('error');
												setShowNotice(true);
											}
										}
									}
								}
								
								// Update the year with the new months array
								activityData.availableDates.group[availableYearIndex] = {
									...activityData.availableDates.group[availableYearIndex],
									months: updatedMonths
								};
							}
						}
						
						return { ...activityData };
					}

					return activityData;
				});
				
				setActivitiesInteractionData(updatedActivitiesInteractionData);
			},
			onError: (error) => {
				setMonthError(activityId, month, year, error);
			}
		});
	}

	const reloadMonthDates = (activityId: number, month: number, year: number) => {
		//Clear the month's data
		const updatedActivitiesInteractionData = activitiesInteractionData.map(activityData => {
			if (activityData.activityId === activityId) {
				const availableYearIndex = activityData.availableDates.group.findIndex(y => y.year === year);
				if (availableYearIndex !== -1) {
					// Clone the months array to avoid direct state mutation
					const updatedMonths = [...activityData.availableDates.group[availableYearIndex].months];
					const monthIndex = updatedMonths.findIndex(m => m.month === month);
					if (monthIndex !== -1) {
						// Set loading to true for the found month
						updatedMonths[monthIndex] = { ...updatedMonths[monthIndex], isLoading: true, fetchError: null, days: [] };
						// Update the year with the new months array
						activityData.availableDates.group[availableYearIndex] = {
							...activityData.availableDates.group[availableYearIndex],
							months: updatedMonths
						};
					}
				}
				return { ...activityData };
			}
			return activityData;
		});

		// Update the activitiesInteractionData state with the new data
		setActivitiesInteractionData(updatedActivitiesInteractionData);

		//Fetch new month
		fetchNewMonth(activityId, month, year);
	}

	return (
		<>
			<ActivityShopUIContext.Provider value={{ addToCart, removeFromCart, modifyPax, getPaxQtyOfType, getTotalPaxQty, getTotalPrice, paxArr, selectedCurrencyCode, handleCurrencyChange, selectedActivityId, setSelectedActivityId, activitiesInteractionData, getActivityInteractionData, toggleAddonSelection, updateActivityDate, updateActivityDeparture, getActivityAvailableMonthDates, removeAllGroupMonthData, reloadMonthDates, getItemsInCartQty, cartBarWrapperElement, setCartBarWrapperElement, fetchSelectedDeparture, applyAgentDiscount, applyAgentSeasons }}>
				{children}
			</ActivityShopUIContext.Provider>
			<GeneralDialog 
				isOpen={showNotice}
				onClose={() => {setShowNotice(false)}}
				title={noticeTitle}
				content={noticeContent}
				type={noticeType}
			/>
		</>
	);
};

export default ActivityShopUIContext;